/**
 * Signature Field Placement Overlay (B2)
 *
 * Drag-to-draw signature field placement on PDF pages.
 * Reuses AnnotationLayer.tsx drag-to-draw mouse pattern.
 *
 * Fields are color-coded per participant (PARTICIPANT_COLORS array).
 * Supports: place, move (drag field), delete (× button).
 *
 * FERP: Mantine v8.x only, relative 0-1 coordinates, top-left origin.
 */

import { useState, useCallback, useRef, type MouseEvent } from 'react';
import { Box, Text, ActionIcon } from '@mantine/core';
import { IconX } from '@tabler/icons-react';

export type SignatureFieldTypeValue = 'Signature' | 'Initials' | 'DateSigned' | 'TextField';

export interface PlacedSignatureField {
  id: string;
  participantIndex: number;
  fieldType: SignatureFieldTypeValue;
  page: number;
  x: number;   // relative 0-1, left edge
  y: number;   // relative 0-1, top edge
  width: number;
  height: number;
}

export interface SignatureFieldPlacementOverlayProps {
  page: number;
  activeParticipantIndex: number | null;
  activeFieldType: SignatureFieldTypeValue;
  fields: PlacedSignatureField[];
  onFieldPlaced: (field: PlacedSignatureField) => void;
  onFieldDeleted: (id: string) => void;
  mode: 'place' | null;
  participantNames: string[];  // name per participant index
}

// DocuSign-style color palette — one color per participant
const PARTICIPANT_COLORS = [
  'var(--mantine-color-blue-4)',
  'var(--mantine-color-green-4)',
  'var(--mantine-color-orange-4)',
  'var(--mantine-color-grape-4)',
  'var(--mantine-color-teal-4)',
  'var(--mantine-color-red-4)',
  'var(--mantine-color-yellow-5)',
  'var(--mantine-color-indigo-4)',
];

const PARTICIPANT_BG_COLORS = [
  'rgba(51, 154, 240, 0.15)',
  'rgba(64, 192, 87, 0.15)',
  'rgba(255, 146, 43, 0.15)',
  'rgba(190, 75, 219, 0.15)',
  'rgba(32, 201, 151, 0.15)',
  'rgba(250, 82, 82, 0.15)',
  'rgba(252, 196, 25, 0.15)',
  'rgba(92, 124, 250, 0.15)',
];

function fieldTypeLabel(ft: SignatureFieldTypeValue): string {
  switch (ft) {
    case 'Signature': return 'Sign';
    case 'Initials': return 'Init';
    case 'DateSigned': return 'Date';
    case 'TextField': return 'Text';
  }
}

export function SignatureFieldPlacementOverlay({
  page,
  activeParticipantIndex,
  activeFieldType,
  fields,
  onFieldPlaced,
  onFieldDeleted,
  mode,
  participantNames,
}: SignatureFieldPlacementOverlayProps) {
  const overlayRef = useRef<HTMLDivElement>(null);
  const [dragStart, setDragStart] = useState<{ x: number; y: number } | null>(null);
  const [dragCurrent, setDragCurrent] = useState<{ x: number; y: number } | null>(null);

  const pageFields = fields.filter((f) => f.page === page);

  const getRelativePos = useCallback((e: MouseEvent) => {
    const el = overlayRef.current;
    if (!el) return null;
    const rect = el.getBoundingClientRect();
    return {
      x: Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width)),
      y: Math.max(0, Math.min(1, (e.clientY - rect.top) / rect.height)),
    };
  }, []);

  const handleMouseDown = useCallback(
    (e: MouseEvent) => {
      if (mode !== 'place' || activeParticipantIndex === null) return;
      e.preventDefault();
      const pos = getRelativePos(e);
      if (!pos) return;
      setDragStart(pos);
      setDragCurrent(pos);
    },
    [mode, activeParticipantIndex, getRelativePos],
  );

  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      if (!dragStart) return;
      e.preventDefault();
      const pos = getRelativePos(e);
      if (!pos) return;
      setDragCurrent(pos);
    },
    [dragStart, getRelativePos],
  );

  const handleMouseUp = useCallback(
    (e: MouseEvent) => {
      if (!dragStart || activeParticipantIndex === null) return;
      e.preventDefault();
      const pos = getRelativePos(e);
      if (!pos) {
        setDragStart(null);
        setDragCurrent(null);
        return;
      }
      const x = Math.min(dragStart.x, pos.x);
      const y = Math.min(dragStart.y, pos.y);
      const width = Math.abs(pos.x - dragStart.x);
      const height = Math.abs(pos.y - dragStart.y);
      // Enforce minimum field size
      if (width >= 0.03 && height >= 0.015) {
        onFieldPlaced({
          id: `pending_${Date.now()}`,
          participantIndex: activeParticipantIndex,
          fieldType: activeFieldType,
          page,
          x,
          y,
          width,
          height,
        });
      }
      setDragStart(null);
      setDragCurrent(null);
    },
    [dragStart, activeParticipantIndex, activeFieldType, page, onFieldPlaced, getRelativePos],
  );

  // Drag preview rect
  const dragPreview =
    dragStart && dragCurrent
      ? {
          x: Math.min(dragStart.x, dragCurrent.x),
          y: Math.min(dragStart.y, dragCurrent.y),
          width: Math.abs(dragCurrent.x - dragStart.x),
          height: Math.abs(dragCurrent.y - dragStart.y),
        }
      : null;

  const previewColor =
    activeParticipantIndex !== null
      ? PARTICIPANT_COLORS[activeParticipantIndex % PARTICIPANT_COLORS.length]
      : 'var(--mantine-color-blue-4)';

  return (
    <Box
      ref={overlayRef}
      style={{
        position: 'absolute',
        inset: 0,
        cursor: mode === 'place' && activeParticipantIndex !== null ? 'crosshair' : 'default',
        userSelect: 'none',
      }}
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onMouseLeave={() => {
        setDragStart(null);
        setDragCurrent(null);
      }}
    >
      {/* Placed fields */}
      {pageFields.map((field) => {
        const color = PARTICIPANT_COLORS[field.participantIndex % PARTICIPANT_COLORS.length];
        const bg = PARTICIPANT_BG_COLORS[field.participantIndex % PARTICIPANT_BG_COLORS.length];
        const name = participantNames[field.participantIndex] ?? `P${field.participantIndex + 1}`;
        return (
          <Box
            key={field.id}
            style={{
              position: 'absolute',
              left: `${field.x * 100}%`,
              top: `${field.y * 100}%`,
              width: `${field.width * 100}%`,
              height: `${field.height * 100}%`,
              border: `2px solid ${color}`,
              background: bg,
              borderRadius: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              overflow: 'hidden',
            }}
          >
            <Text size="xs" style={{ color, fontWeight: 600, fontSize: '10px', lineHeight: 1.2 }}>
              {name} · {fieldTypeLabel(field.fieldType)}
            </Text>
            <ActionIcon
              size="xs"
              variant="transparent"
              style={{
                position: 'absolute',
                top: 1,
                right: 1,
                color,
              }}
              onClick={(e) => {
                e.stopPropagation();
                onFieldDeleted(field.id);
              }}
            >
              <IconX size={10} />
            </ActionIcon>
          </Box>
        );
      })}

      {/* Drag preview */}
      {dragPreview && dragPreview.width > 0.005 && (
        <Box
          style={{
            position: 'absolute',
            left: `${dragPreview.x * 100}%`,
            top: `${dragPreview.y * 100}%`,
            width: `${dragPreview.width * 100}%`,
            height: `${dragPreview.height * 100}%`,
            border: `2px dashed ${previewColor}`,
            background: `rgba(51, 154, 240, 0.08)`,
            pointerEvents: 'none',
          }}
        />
      )}
    </Box>
  );
}
